// =========================================================================
// Confirm Modal (W4)
// =========================================================================
// spec/closing_river_web_mvp_v2.md
//   §「画面フロー」 辺タップ → Preview → 「決定」 → 実行
//   §11.2 「決定」 タップ後は不可逆
//   §「ハプティック」 navigator.vibrate(15) (guard で)
//
// 役割:
//   - controller から show(preview, onConfirm, onCancel) で呼び出される
//   - 半透明 overlay + カード (preview-bar より詳しい説明) + 決定/キャンセル
//   - 決定タップ後は即時 hide + onConfirm (= 不可逆、再 push できない)
//   - ESC キーで cancel (デスクトップ)、overlay 外側タップで cancel
//
// 注意:
//   - このファイルは controller 側ロジックを持たない。onConfirm/onCancel を
//     呼ぶ pure UI コンポーネント。
//   - 入力ロックの一部 (アニメ中の disable) は controller 側責務。modal 自身は
//     1回 hide すれば再 show されるまで何もしない。
// =========================================================================

import type { EdgePreview } from './preview.js';
import { formatPreviewValues, formatPreviewLabel, __ensureW4Styles } from './preview.js';

// -------------------------------------------------------------------------
// 公開 API
// -------------------------------------------------------------------------

export interface ConfirmModalHandle {
  /** モーダルを表示。決定で onConfirm、キャンセル系で onCancel。同時に複数 show しない */
  show(preview: EdgePreview, onConfirm: () => void, onCancel: () => void): void;
  /** 外部からの強制 hide (例: 勝敗到達で controller がクリーンアップする) */
  hide(): void;
  /** イベントリスナーと DOM を解放 */
  destroy(): void;
}

/**
 * モーダルを container 配下に1個生成して返す。
 * container は通常 document.body (overlay は position: fixed なので位置はどこでも可)。
 */
export function createConfirmModal(container: HTMLElement): ConfirmModalHandle {
  __ensureW4Styles();

  const overlay = document.createElement('div');
  overlay.className = 'cr-confirm-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.display = 'none';

  const card = document.createElement('div');
  card.className = 'cr-confirm-card';
  // overlay 外側タップを区別するため card クリックは伝播停止
  card.addEventListener('click', (ev) => ev.stopPropagation());

  const title = document.createElement('div');
  title.className = 'cr-confirm-card__title';
  title.textContent = 'この辺を確定しますか？';

  const value = document.createElement('div');
  value.className = 'cr-confirm-card__value';

  const label = document.createElement('div');
  label.className = 'cr-confirm-card__label';

  const hint = document.createElement('div');
  hint.className = 'cr-confirm-card__hint';
  hint.textContent = '決定後は取消できません';

  const buttons = document.createElement('div');
  buttons.className = 'cr-confirm-card__buttons';

  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'cr-confirm-ghost';
  cancelBtn.textContent = 'キャンセル';

  const confirmBtn = document.createElement('button');
  confirmBtn.type = 'button';
  confirmBtn.className = 'cr-confirm-primary';
  confirmBtn.textContent = '決定';

  buttons.appendChild(cancelBtn);
  buttons.appendChild(confirmBtn);

  card.appendChild(title);
  card.appendChild(value);
  card.appendChild(label);
  card.appendChild(hint);
  card.appendChild(buttons);

  overlay.appendChild(card);
  container.appendChild(overlay);

  // -----------------------------------------------------------------------
  // ハンドラ (show ごとに差し替え、不可逆性のため一度呼んだら無効化)
  // -----------------------------------------------------------------------
  let activeOnConfirm: (() => void) | null = null;
  let activeOnCancel: (() => void) | null = null;
  let isOpen = false;

  const doConfirm = () => {
    if (!isOpen) return;
    const cb = activeOnConfirm;
    // §11.2 不可逆: 呼び出し前に handler を null にして二重発火を防ぐ
    activeOnConfirm = null;
    activeOnCancel = null;
    closeInternal();
    // ハプティック (guard 必須、iOS は無視されるが call 自体は安全)
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(15);
      } catch {
        // 一部 browser で SecurityError 等。failsafe で握りつぶす
      }
    }
    if (cb) cb();
  };

  const doCancel = () => {
    if (!isOpen) return;
    const cb = activeOnCancel;
    activeOnConfirm = null;
    activeOnCancel = null;
    closeInternal();
    if (cb) cb();
  };

  const closeInternal = () => {
    isOpen = false;
    overlay.removeAttribute('data-state');
    overlay.setAttribute('aria-hidden', 'true');
    // フェードアウト後 display:none。styles.css 側で transition 設定済。
    // 安全のため即座に hidden 化 (cleanup レース回避)。
    overlay.style.display = 'none';
  };

  // -----------------------------------------------------------------------
  // event listeners (destroy で全て解除)
  // -----------------------------------------------------------------------
  const onConfirmClick = () => doConfirm();
  const onCancelClick = () => doCancel();
  const onOverlayClick = () => doCancel(); // 外側タップで cancel
  const onKeyDown = (ev: KeyboardEvent) => {
    if (!isOpen) return;
    if (ev.key === 'Escape') {
      ev.preventDefault();
      doCancel();
    } else if (ev.key === 'Enter') {
      // デスクトップで Enter = 決定 (ハプティックは vibrate guard で skip される)
      ev.preventDefault();
      doConfirm();
    }
  };

  confirmBtn.addEventListener('click', onConfirmClick);
  cancelBtn.addEventListener('click', onCancelClick);
  overlay.addEventListener('click', onOverlayClick);
  // capture: true ではなく window レベルで listen (modal 重複時の最後の盾)
  window.addEventListener('keydown', onKeyDown);

  // -----------------------------------------------------------------------
  // 公開 API
  // -----------------------------------------------------------------------
  const handle: ConfirmModalHandle = {
    show(preview, onConfirm, onCancel) {
      // 既に開いている場合は前の cancel を発火させてから新規 show
      // (controller 側でこの状況にしないのが期待だが、安全装置)
      if (isOpen) {
        const prev = activeOnCancel;
        activeOnConfirm = null;
        activeOnCancel = null;
        if (prev) prev();
      }
      activeOnConfirm = onConfirm;
      activeOnCancel = onCancel;
      // テキスト更新
      value.textContent = formatPreviewValues(preview);
      label.textContent = formatPreviewLabel(preview);
      // 表示
      overlay.style.display = 'flex';
      overlay.setAttribute('aria-hidden', 'false');
      isOpen = true;
      // 次フレームで transition トリガ
      requestAnimationFrame(() => {
        overlay.setAttribute('data-state', 'open');
      });
      // 決定ボタンに focus (キーボードユーザー)。失敗しても無視
      try {
        confirmBtn.focus({ preventScroll: true });
      } catch {
        // 古い browser
      }
    },
    hide() {
      if (!isOpen) return;
      activeOnConfirm = null;
      activeOnCancel = null;
      closeInternal();
    },
    destroy() {
      confirmBtn.removeEventListener('click', onConfirmClick);
      cancelBtn.removeEventListener('click', onCancelClick);
      overlay.removeEventListener('click', onOverlayClick);
      window.removeEventListener('keydown', onKeyDown);
      if (overlay.parentElement) overlay.parentElement.removeChild(overlay);
      isOpen = false;
      activeOnConfirm = null;
      activeOnCancel = null;
    },
  };

  return handle;
}
